import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Users,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Archive,
  Search,
  Filter,
  Phone,
  Mail,
  User,
  Edit,
  X,
  Plus
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ENROLLMENT_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'amber', icon: Clock },
  { value: 'approved', label: 'Approved', color: 'emerald', icon: CheckCircle2 },
  { value: 'rejected', label: 'Rejected', color: 'red', icon: XCircle },
  { value: 'archived', label: 'Archived', color: 'slate', icon: Archive }
];

const MinistryEnrollmentsSection = ({ adminUser }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMinistry, setFilterMinistry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [enrollmentsRes, ministriesRes] = await Promise.all([
        axios.get(`${API}/ministry-enrollments/admin`, {
          params: { status: filterStatus, ministry_id: filterMinistry }
        }),
        axios.get(`${API}/ministries`)
      ]);
      setEnrollments(enrollmentsRes.data);
      setMinistries(ministriesRes.data);
      
      // Calculate stats
      const pending = enrollmentsRes.data.filter(e => e.status === 'pending' || !e.status).length;
      const approved = enrollmentsRes.data.filter(e => e.status === 'approved').length;
      setStats({ total: enrollmentsRes.data.length, pending, approved });
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  }, [filterStatus, filterMinistry]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusChange = async (enrollmentId, newStatus) => {
    try {
      await axios.put(`${API}/ministry-enrollments/${enrollmentId}`, {
        status: newStatus,
        approved_by: adminUser?.full_name || 'Admin'
      });
      loadData();
      if (selectedEnrollment?.id === enrollmentId) {
        setSelectedEnrollment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleSaveNotes = async (enrollmentId) => {
    try {
      await axios.put(`${API}/ministry-enrollments/${enrollmentId}`, {
        admin_notes: adminNotes
      });
      loadData();
      setSelectedEnrollment(prev => ({ ...prev, admin_notes: adminNotes }));
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const handleOpenEnrollment = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setAdminNotes(enrollment.admin_notes || '');
  };

  const filteredEnrollments = enrollments.filter(e =>
    e.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.ministry_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.member_phone?.includes(searchQuery)
  );

  const getStatusInfo = (status) => {
    return ENROLLMENT_STATUSES.find(s => s.value === status) || ENROLLMENT_STATUSES[0];
  };

  const getStatusBadge = (status) => {
    const s = getStatusInfo(status || 'pending');
    const colorMap = {
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      red: 'bg-red-100 text-red-700 border-red-200',
      slate: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    const IconComponent = s.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${colorMap[s.color]}`}>
        <IconComponent className="w-3 h-3" />
        {s.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="ministry-enrollments-section">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Ministry Enrollments</h2>
              <p className="text-sm text-slate-500">Manage ministry signup requests</p>
            </div>
          </div>
          <motion.button
            onClick={loadData}
            className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
          <p className="text-sm text-slate-600">Total Enrollments</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <p className="text-sm text-amber-700">Pending Review</p>
          </div>
          <p className="text-2xl font-bold text-amber-900">{stats.pending}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <p className="text-sm text-emerald-700">Approved</p>
          </div>
          <p className="text-2xl font-bold text-emerald-900">{stats.approved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, ministry, or phone..."
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-teal-400 outline-none"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-600 flex items-center gap-1">
              <Filter className="w-4 h-4" />
              Status:
            </span>
            <button
              onClick={() => setFilterStatus('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                !filterStatus ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All
            </button>
            {ENROLLMENT_STATUSES.map(status => (
              <button
                key={status.value}
                onClick={() => setFilterStatus(status.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filterStatus === status.value
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>

          {/* Ministry Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Ministry:</span>
            <select
              value={filterMinistry}
              onChange={e => setFilterMinistry(e.target.value)}
              className="border-2 border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:border-teal-400 outline-none"
            >
              <option value="">All Ministries</option>
              {ministries.map(ministry => (
                <option key={ministry.id} value={ministry.id}>{ministry.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Enrollments List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin text-teal-500" />
          </div>
        ) : filteredEnrollments.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-slate-100">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 font-medium">No enrollments found</p>
            <p className="text-sm text-slate-500 mt-1">Ministry enrollment requests will appear here</p>
          </div>
        ) : (
          filteredEnrollments.map((enrollment, index) => (
            <motion.div
              key={enrollment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className={`bg-white rounded-xl p-4 shadow-lg border cursor-pointer transition-all hover:shadow-xl ${
                (!enrollment.status || enrollment.status === 'pending')
                  ? 'border-amber-200 bg-gradient-to-r from-amber-50/30 to-white'
                  : 'border-slate-100'
              }`}
              onClick={() => handleOpenEnrollment(enrollment)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`p-2 rounded-xl ${
                    (!enrollment.status || enrollment.status === 'pending')
                      ? 'bg-amber-100'
                      : enrollment.status === 'approved'
                        ? 'bg-emerald-100'
                        : 'bg-slate-100'
                  }`}>
                    <User className={`w-5 h-5 ${
                      (!enrollment.status || enrollment.status === 'pending')
                        ? 'text-amber-600'
                        : enrollment.status === 'approved'
                          ? 'text-emerald-600'
                          : 'text-slate-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-800">{enrollment.member_name}</span>
                      {getStatusBadge(enrollment.status)}
                    </div>
                    <p className="text-sm text-teal-600 font-medium">{enrollment.ministry_name}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      {enrollment.member_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {enrollment.member_phone}
                        </span>
                      )}
                      {enrollment.member_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {enrollment.member_email}
                        </span>
                      )}
                      {enrollment.created_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(enrollment.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                {(!enrollment.status || enrollment.status === 'pending') && (
                  <div className="flex gap-2 shrink-0">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(enrollment.id, 'approved');
                      }}
                      className="p-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(enrollment.id, 'rejected');
                      }}
                      className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <XCircle className="w-5 h-5" />
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Enrollment Detail Modal */}
      <AnimatePresence>
        {selectedEnrollment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedEnrollment(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(selectedEnrollment.status)}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedEnrollment.member_name}</h3>
                    <p className="text-teal-600 font-medium">{selectedEnrollment.ministry_name}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedEnrollment(null)} 
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Contact Info */}
                <div className="p-4 rounded-xl bg-slate-50 space-y-3">
                  <h4 className="font-semibold text-slate-700">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${selectedEnrollment.member_phone}`} className="text-blue-600 hover:underline">
                        {selectedEnrollment.member_phone}
                      </a>
                    </div>
                    {selectedEnrollment.member_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <a href={`mailto:${selectedEnrollment.member_email}`} className="text-blue-600 hover:underline">
                          {selectedEnrollment.member_email}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">Applied: {formatDate(selectedEnrollment.created_at || selectedEnrollment.enrolled_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2">Admin Notes</h4>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-teal-400 outline-none"
                    rows={3}
                    placeholder="Add internal notes about this enrollment..."
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                  />
                  <motion.button
                    onClick={() => handleSaveNotes(selectedEnrollment.id)}
                    className="mt-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Save Notes
                  </motion.button>
                </div>

                {/* Approval Info */}
                {selectedEnrollment.status === 'approved' && selectedEnrollment.approved_at && (
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Approved by {selectedEnrollment.approved_by}</span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">{formatDate(selectedEnrollment.approved_at)}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer - Actions */}
              <div className="p-6 border-t border-slate-100 bg-slate-50">
                <div className="flex flex-wrap gap-3">
                  {(!selectedEnrollment.status || selectedEnrollment.status === 'pending') && (
                    <>
                      <motion.button
                        onClick={() => handleStatusChange(selectedEnrollment.id, 'approved')}
                        className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        Approve
                      </motion.button>
                      <motion.button
                        onClick={() => handleStatusChange(selectedEnrollment.id, 'rejected')}
                        className="px-6 py-3 rounded-xl bg-red-100 text-red-700 font-medium flex items-center justify-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <XCircle className="w-5 h-5" />
                        Reject
                      </motion.button>
                    </>
                  )}
                  {selectedEnrollment.status !== 'archived' && (
                    <motion.button
                      onClick={() => handleStatusChange(selectedEnrollment.id, 'archived')}
                      className="px-6 py-3 rounded-xl bg-slate-200 text-slate-700 font-medium flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Archive className="w-5 h-5" />
                      Archive
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MinistryEnrollmentsSection;
